import React, { useEffect, useRef } from 'react';
import scrollParent from '@utils/scrollParent';

/** How far below the visible area the next chunk is asked for, so it is there before the user gets to it. */
const PRELOAD_DISTANCE_PX = 800;

/**
 * An invisible line under the last row: once it comes within {@link PRELOAD_DISTANCE_PX} of the
 * visible area of whatever scrolls the table, `onNear` asks for the next chunk.
 *
 * Remount it (a new `key`) after each chunk: an observer only reports changes, so a line still near
 * the bottom after a short chunk would never report again. A fresh one reports where it is at once.
 */
const LoadMoreSentinel: React.FC<{ onNear: () => void }> = ({ onNear }) => {
  const ref = useRef<HTMLDivElement>(null);
  const onNearRef = useRef(onNear);
  onNearRef.current = onNear;

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(entries => entries.some(entry => entry.isIntersecting) && onNearRef.current(), {
      root: scrollParent(element),
      rootMargin: `0px 0px ${PRELOAD_DISTANCE_PX}px 0px`,
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return <div ref={ref} aria-hidden className="h-px" data-testid="table-load-more" />;
};

export default LoadMoreSentinel;
